import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Section } from "@/components/Section";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { JsonLd } from "@/components/JsonLd";
import { FaqAccordion, type FaqItem } from "@/components/FaqAccordion";
import { bnPageHead } from "@/lib/bn-head";
import { faqSchema } from "@/lib/schema";
import type { ActionPlan } from "@/lib/action-plan";
import { ActionPlanForm, ActionPlanView } from "@/components/action-plan/ActionPlanTool";

export const Route = createFileRoute("/bn/ai-action-plan")({
  head: () =>
    bnPageHead({
      path: "/bn/ai-action-plan",
      enPath: "/ai-action-plan",
      title: "মার্কেটিংয়ে এআই কীভাবে ব্যবহার করবেন — ফ্রি অ্যাকশন প্ল্যান",
      desc: "আপনার ওয়েবসাইটের লেখা ও ব্যবসার লক্ষ্য থেকে ফ্রি এসইও, AEO ও GEO অ্যাকশন প্ল্যান বানান — অগ্রাধিকারসহ, বাংলায়। সঙ্গে এআই মার্কেটিং পরিকল্পনার প্রশ্নোত্তর।",
      keywords: ["ai marketing bangla", "মার্কেটিংয়ে এআই", "এআই মার্কেটিং পরিকল্পনা", "ai seo plan bangla"],
    }),
  component: BnActionPlanPage,
});

const FAQS: FaqItem[] = [
  { q: "মার্কেটিংয়ে এআই কীভাবে ব্যবহার করব?", a: "একটি লক্ষ্য আর একটি নির্দিষ্ট কাজ দিয়ে শুরু করুন — যেমন গ্রাহকের প্রশ্ন গুছানো, কনটেন্টের আউটলাইন বানানো বা ওয়েবসাইটের কোন কাজ আগে করবেন তা ঠিক করা। এআই-কে আপনার গ্রাহক, অফার, বাজার ও বর্তমান লেখা দিন, তারপর তার পরামর্শ গ্রাহকের আসল চাহিদার সঙ্গে মিলিয়ে দেখুন। এই প্ল্যানটি শুরুর জায়গা, নিজের বিচার-বিবেচনার বিকল্প নয়।" },
  { q: "ব্যবসার জন্য এআই মার্কেটিং কৌশল কীভাবে বানাব?", a: "আগে ঠিক করুন কী ফল চান: বেশি মানসম্মত জিজ্ঞাসা, বিক্রি না পুরনো গ্রাহক ফেরানো। আপনার গ্রাহক কে, এখনকার ফল কী, আর কোন চ্যানেল নিয়মিত চালাতে পারবেন তা লিখে রাখুন। তারপর এআই-কে আসল তথ্য দিয়ে কারণ, পরিশ্রম ও অগ্রাধিকারসহ কাজের তালিকা চান। এই টুল সার্চে দেখা যাওয়ার দিকটা দেখে; পেইড অ্যাড, ইমেইল বা সোশ্যাল মিডিয়ার পূর্ণ কৌশল নয়।" },
  { q: "ছোট ব্যবসা প্রথমে কোন এআই টুল ব্যবহার করবে?", a: "অনেক টুল একসঙ্গে না কিনে এমন কাজের জন্য টুল বাছুন যা আপনি এমনিতেই করেন। লেখার সহকারী আউটলাইন ও প্রথম খসড়ায় সাহায্য করে; পরিকল্পনার টুল ওয়েবসাইটের কাজ গুছিয়ে দেয়। খরচ, গোপনীয়তার শর্ত আর যাচাইয়ের সময় তুলনা করুন। যে টুল সঠিকতা না কমিয়ে সময় বাঁচায়, সেটাই রাখুন।" },
  { q: "এআই সার্চের জন্য মার্কেটিং কৌশল কীভাবে বদলাব?", a: "কাজের, সহজে পড়া যায় এমন পেজ আর সাধারণ এসইও-ই ভিত্তি। গ্রাহকের নির্দিষ্ট প্রশ্নের পরিষ্কার উত্তর দিন, সার্ভিসের বর্ণনা সব জায়গায় একই রাখুন, আর দাবির পক্ষে এমন প্রমাণ দিন যা দেখে যাচাই করা যায়। এআই কোন উত্তর বাদ পড়েছে তা খুঁজতে সাহায্য করে, কিন্তু ChatGPT, Gemini বা Google AI Overviews-এ উল্লেখের নিশ্চয়তা দিতে পারে না।" },
  { q: "এআই মার্কেটিং প্ল্যান কাজ করছে কিনা কীভাবে বুঝব?", a: "পরিবর্তনের আগে বর্তমান অবস্থা লিখে রাখুন, তারপর লক্ষ্যের সঙ্গে যুক্ত ফল মাপুন: মানসম্মত লিড, সার্চ থেকে জিজ্ঞাসা বা বিক্রি। সার্চে দেখা ও ক্লিকের জন্য Search Console, ভিজিট ও কনভার্সনের জন্য অ্যানালিটিক্স ব্যবহার করুন। র‍্যাংকিং বা এআই উল্লেখ সহায়ক সংকেত, আয়ের প্রমাণ নয়।" },
  { q: "এআই পরিকল্পনার জন্য কী কী তথ্য দেব?", a: "ব্যবসার ধরন, বাজার, অফার, গ্রাহকের প্রশ্ন, লক্ষ্য আর যে লেখা যাচাই করাতে চান। অগ্রাধিকারসহ তালিকা, কারণ ও পরের ধাপ চান। শুধু সেই লেখাই দিন যা শেয়ার করার অনুমতি আছে — পাসওয়ার্ড, ক্লায়েন্টের গোপন তথ্য বা গ্রাহকের ব্যক্তিগত তথ্য দেবেন না। ওয়েবসাইটের ঠিকানা দিলেও এই টুল সাইটটি লাইভ অডিট করে না; শুধু আপনার দেওয়া লেখা দেখে।" },
];

function BnActionPlanPage() {
  const [plan, setPlan] = useState<ActionPlan | null>(null);

  return (
    <>
      <Breadcrumbs items={[{ name: "হোম", path: "/bn" }, { name: "এআই অ্যাকশন প্ল্যান", path: "/bn/ai-action-plan" }]} />
      <Section headingLevel={1} eyebrow="ফ্রি এআই অ্যাকশন প্ল্যান" title={<>মার্কেটিংয়ে <span className="text-gradient">এআই-এর ব্যবহার।</span></>} className="!pt-12 !pb-12 md:!pt-20 md:!pb-16">
        <p className="max-w-2xl text-lg text-muted-foreground -mt-8 mb-10">
          আপনার ব্যবসার লক্ষ্য আর ওয়েবসাইটের লেখা থেকে সার্চ ও এআই উত্তরে দেখা যাওয়ার জন্য অগ্রাধিকারসহ
          এসইও, AEO ও GEO প্ল্যান বানান। কাজে নামার আগে পরামর্শগুলো যাচাই করে নিন।
        </p>
        <ActionPlanForm lang="bn" onPlan={setPlan} />
      </Section>

      {plan && <ActionPlanView plan={plan} lang="bn" />}

      <Section eyebrow="প্রশ্নোত্তর" title="এআই মার্কেটিং পরিকল্পনার প্রশ্ন।" className="!pt-8 md:!pt-12">
        <div className="max-w-3xl">
          <FaqAccordion items={FAQS} idPrefix="bn-ai-marketing-faq" />
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-sm">
            <Link to="/bn/online-marketing-small-businesses" className="text-primary underline underline-offset-4">ছোট ব্যবসার অনলাইন মার্কেটিং</Link>
            <Link to="/bn/services/aeo" className="text-primary underline underline-offset-4">AEO সার্ভিস</Link>
            <Link to="/bn/case-studies" className="text-primary underline underline-offset-4">আসল প্রজেক্টের কেস স্টাডি</Link>
          </div>
        </div>
        <JsonLd data={faqSchema(FAQS)} />
      </Section>
    </>
  );
}
